import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import VariantPromptPopup from "../variants/VariantPromptPopup";

let nextMessageUid = 1;
const newUid = () => `m${nextMessageUid++}`;

const defaultPromptMessages = () => [
  { uid: newUid(), text: "Not sure where to start?" },
  { uid: newUid(), text: "Take our quick quiz →" },
];

const normalizePromptMessages = (messages) => {
  const normalized = (Array.isArray(messages) ? messages : [])
    .map((message) => ({
      uid: message && message.uid ? message.uid : newUid(),
      text: message && message.text ? message.text : "",
    }));

  return normalized.length > 0 ? normalized : defaultPromptMessages();
};

const parsePromptMessages = (value) => {
  if (Array.isArray(value)) {
    return normalizePromptMessages(value);
  }

  if (value && typeof value === "object") {
    return normalizePromptMessages(Object.values(value));
  }

  try {
    const parsed = JSON.parse(value || "[]");
    return normalizePromptMessages(
      Array.isArray(parsed) ? parsed : Object.values(parsed || {})
    );
  } catch (e) {
    return [{ uid: newUid(), text: "Not sure where to start?" }];
  }
};

const PREVIEW_TABS = ["Triggering", "Expanded", "Collapsed"];

const VariantSettingsPrompt = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  let variant = contextData.objects.variant;
  const isPromptTrigger = variant.options["trigger_type"] == "prompt";
  const isPromptOnClose =
    variant.options["prehook-trigger-on-close"] == "true" &&
    variant.options["close-message-type"] == "prompt";
  const showPromptSettings = isPromptTrigger || isPromptOnClose;
  const promptMessagesOption = variant.options["prompt-messages"];

  const persistedPromptMessages = useMemo(
    () => parsePromptMessages(promptMessagesOption),
    [variant.id, promptMessagesOption]
  );

  const [promptMessages, setPromptMessages] = useState(persistedPromptMessages);
  const [savedPromptMessages, setSavedPromptMessages] = useState(
    persistedPromptMessages
  );
  const [activePreviewTab, setActivePreviewTab] = useState("Expanded");
  const [previewMessageIndex, setPreviewMessageIndex] = useState(0);
  const [previewAnimKey, setPreviewAnimKey] = useState(0);

  useEffect(() => {
    const sameTexts =
      savedPromptMessages.length === persistedPromptMessages.length &&
      savedPromptMessages.every(
        (message, index) => message.text === persistedPromptMessages[index].text
      );
    if (sameTexts) return;

    setPromptMessages(persistedPromptMessages);
    setSavedPromptMessages(persistedPromptMessages);
  }, [persistedPromptMessages]);

  const persistMessages = useCallback((updated) => {
    const normalized = normalizePromptMessages(updated);
    setPromptMessages(normalized);
    setSavedPromptMessages(normalized);
    const toPersist = normalized.map((m) => ({ text: m.text }));
    builder.update([
      {
        object_type: "variants",
        object_id: variant.id,
        setting_name: "[options][prompt-messages]",
        value: JSON.stringify(toPersist),
      },
    ]);
  }, [builder, variant.id]);

  const updateMessage = useCallback((index, text) => {
    setPromptMessages((prev) => prev.map((m, i) => (i === index ? { ...m, text } : m)));
  }, []);

  const promptMessagesRef = useRef(promptMessages);
  useEffect(() => {
    promptMessagesRef.current = promptMessages;
  }, [promptMessages]);

  const saveMessages = useCallback(() => {
    persistMessages(promptMessagesRef.current);
  }, [persistMessages]);

  const addMessage = useCallback(() => {
    setPromptMessages((prev) => [...prev, { uid: newUid(), text: "" }]);
  }, []);

  const removeMessage = useCallback((index) => {
    const current = promptMessagesRef.current;
    if (current.length <= 1) return;
    persistMessages(current.filter((_, i) => i !== index));
  }, [persistMessages]);

  const handleTabChange = useCallback((tab) => {
    setActivePreviewTab(tab);
    setPreviewAnimKey((k) => k + 1);
  }, []);

  const previewMessages = useMemo(
    () => [promptMessages[previewMessageIndex] || promptMessages[0]],
    [promptMessages, previewMessageIndex]
  );

  return (
    <React.Fragment>
      <VariantBuilderSetting
        object={variant}
        setting_name="[options][modal-trigger]"
        setting_type="modal"
        object_type="variants"
        object_id={variant.id}
        value="Edit Messenger Popup"
        button_glyphicon="glyphicon-edit"
        modal_width="!w-[890px]"
        condition={showPromptSettings}
      >
        <div className="flex gap-5 items-start w-full">
          {/* Left column: settings */}
          <div className="flex-1 min-w-0 flex flex-col gap-5">
            {/* Messages section */}
            <div className="col-xs-12 some-vertical-space setting">
              <div className="row">
                <div className="col-xs-12">
                  <div className="mb-[5px] text-xl text-black dark:text-white font-bold">
                    Messenger Popup Messages
                  </div>
                  <p className="field-note">
                    Messages rotate with a typewriter effect. Click to open the
                    popup.
                  </p>
                  <div className="flex flex-col gap-[10px] pt-[10px]">
                    {promptMessages.map((msg, index) => (
                      <form
                        key={msg.uid}
                        className="flex gap-x-[7.5px] items-start w-full"
                        onSubmit={(e) => {
                          e.preventDefault();
                          saveMessages();
                        }}
                      >
                        <div className="flex-grow min-w-[100px]">
                          <input
                            type="text"
                            className="w-full p-[8.5px] bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-[7.5px] border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
                            value={msg.text}
                            placeholder={`Message ${index + 1}`}
                            onChange={(e) => updateMessage(index, e.target.value)}
                            onBlur={saveMessages}
                          />
                        </div>
                        {previewMessageIndex !== index && (
                          <button
                            type="button"
                            className="w-[75px] py-3 px-3 rounded-lg text-xl ml-1 mr-1 semibold cursor-pointer shrink-0 border border-solid border-slate-200 bg-white text-black hover:bg-slate-50"
                            onClick={() => setPreviewMessageIndex(index)}
                          >
                            Preview
                          </button>
                        )}
                        <button
                          type="button"
                          className={`h-[30px] p-0 px-2 pt-1 bg-white dark:bg-slate-950 cursor-pointer border-2 border-solid border-white dark:border-slate-950 rounded-lg text-[24px] leading-none ${
                            promptMessages.length <= 1
                              ? "text-[#a9a9a9] cursor-default"
                              : "text-black dark:text-white hover:bg-slate-100 hover:border-slate-100"
                          }`}
                          onClick={() => removeMessage(index)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </form>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="w-full pt-5 p-2 pl-0 text-left bg-transparent border-none text-xl text-blue-600 dark:text-blue-200 dark:hover:text-white"
                    onClick={addMessage}
                  >
                    + Add Message
                  </button>
                </div>
              </div>
            </div>

            <VariantBuilderSetting
              object={variant}
              setting_name="[options][prompt-rotation-speed]"
              setting_type="number"
              object_type="variants"
              label="Rotate every (seconds)"
              value={
                variant.options["prompt-rotation-speed"]
                  ? variant.options["prompt-rotation-speed"]
                  : "3"
              }
              condition={showPromptSettings}
              min={1}
              note="Seconds between message rotations"
            />

            <VariantBuilderSetting
              object={variant}
              object_id={variant.id}
              setting_name="[options][prompt-icon]"
              setting_type="icon"
              object_type="variants"
              label="Button icon"
              value={variant.options["prompt-icon"] || null}
              default_value="fas fa-wand-magic-sparkles"
              skip_default_update={true}
              show_reset={true}
              condition={showPromptSettings}
            />

            <VariantBuilderSetting
              object={variant}
              setting_name="[options][prompt-icon-color]"
              setting_type="color_v2"
              object_type="variants"
              label="Button Icon Color"
              value={variant.options["prompt-icon-color"] || ""}
              placeholder="#fff"
              show_reset={true}
              allow_empty={true}
              condition={showPromptSettings && !!variant.options["prompt-icon"]}
            />

            <VariantBuilderSetting
              object={variant}
              setting_name="[options][prompt-button-color]"
              setting_type="color_v2"
              object_type="variants"
              label="Button color"
              value={variant.options["prompt-button-color"] || ""}
              placeholder="#0D42E4"
              show_reset={true}
              allow_empty={true}
              condition={showPromptSettings}
              gradient={true}
            />

            <VariantBuilderSetting
              object={variant}
              setting_name="[options][prompt-position]"
              setting_type="dropdown"
              object_type="variants"
              label="Position"
              options={[
                { value: "left", text: "Left" },
                { value: "center", text: "Center" },
                { value: "right", text: "Right" },
              ]}
              value={
                variant.options["prompt-position"]
                  ? variant.options["prompt-position"]
                  : "center"
              }
              condition={showPromptSettings}
            />

            <VariantBuilderSetting
              object={variant}
              setting_name="[options][prompt-retrigger-message]"
              setting_type="text"
              object_type="variants"
              label="Override message if triggered in session"
              value={variant.options["prompt-retrigger-message"] || ""}
              condition={
                isPromptTrigger &&
                variant.options["prompt-retrigger-enabled"] !== "false"
              }
              placeholder="Optional custom message on re-trigger"
            />
          </div>

          {/* Right column: preview panel */}
          <div className="shrink-0 w-[424px] bg-[#e5e7eb] rounded-[10px] flex flex-col gap-[20px] items-center py-[10px] self-stretch min-h-[400px]">
            {/* Tab switcher */}
            <div className="px-[15px]">
              <div className="bg-[rgba(255,255,255,0.3)] border border-solid border-white flex items-start p-[4px] rounded-[68px]">
                {PREVIEW_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => handleTabChange(tab)}
                    className={`flex gap-[8px] h-[32px] items-center justify-center px-[12px] rounded-[56px] w-[74px] border-none cursor-pointer text-[12px] font-[475] tracking-[-0.05px] whitespace-nowrap transition-all ${
                      activePreviewTab === tab
                        ? "bg-white shadow-[0px_1.5px_2px_rgba(10,9,11,0.07)] text-black"
                        : "bg-transparent text-black"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview area */}
            <div className="flex-1 flex items-end justify-center w-full px-[15px] pb-[10px]">
              <div
                key={previewAnimKey}
                className={`cta${props.cta.id} cf-prompt-popup`}
                data-cta-id={props.cta.id}
                style={{ paddingBottom: "0px", position: "sticky !important", bottom: "20px !important", width: "fit-content", maxWidth: "100%" }}
              >
                <VariantPromptPopup
                  {...props}
                  builder={true}
                  device={contextData.device}
                  messages={previewMessages}
                  previewState={activePreviewTab.toLowerCase()}
                  user={
                    contextData.objects.users.filter(
                      (user) => user.id + "" == variant.user_id + ""
                    )[0]
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </VariantBuilderSetting>

      <VariantBuilderSetting
        object={variant}
        setting_name="[options][prompt-position]"
        setting_type="dropdown"
        object_type="variants"
        label="Position"
        options={[
          { value: "left", text: "Left" },
          { value: "center", text: "Center" },
          { value: "right", text: "Right" },
        ]}
        value={
          variant.options["prompt-position"]
            ? variant.options["prompt-position"]
            : "center"
        }
        condition={showPromptSettings}
      />

      <VariantBuilderSetting
        object={variant}
        setting_name="[options][prompt-initial-delay]"
        setting_type="number"
        object_type="variants"
        label="Appear after (seconds)"
        value={
          variant.options["prompt-initial-delay"] !== undefined
            ? variant.options["prompt-initial-delay"]
            : "5"
        }
        condition={isPromptTrigger}
        min={0}
        note="Seconds after page load before appearing"
      />

      <VariantBuilderSetting
        object={variant}
        setting_name="[options][prompt-initial-scroll]"
        setting_type="range"
        object_type="variants"
        label="Or after scroll depth (%)"
        value={
          variant.options["prompt-initial-scroll"]
            ? variant.options["prompt-initial-scroll"]
            : "25"
        }
        condition={isPromptTrigger}
        min={0}
        max={100}
        range_step={1}
      />

      <VariantBuilderSetting
        object={variant}
        setting_name="[options][prompt-retrigger-enabled]"
        setting_type="checkbox"
        object_type="variants"
        label="Re-appear after popup close"
        value={
          variant.options["prompt-retrigger-enabled"] !== undefined
            ? variant.options["prompt-retrigger-enabled"]
            : "true"
        }
        condition={isPromptTrigger}
      />

      <VariantBuilderSetting
        object={variant}
        setting_name="[options][prompt-retrigger-scroll]"
        setting_type="range"
        object_type="variants"
        label="Re-appear at scroll depth (%)"
        value={
          variant.options["prompt-retrigger-scroll"]
            ? variant.options["prompt-retrigger-scroll"]
            : "75"
        }
        condition={
          isPromptTrigger &&
          variant.options["prompt-retrigger-enabled"] !== "false"
        }
        min={0}
        max={100}
        range_step={1}
      />

    </React.Fragment>
  );
};

VariantSettingsPrompt.propTypes = {
  cta: PropTypes.object.isRequired,
  variant: PropTypes.object.isRequired,
  current_user: PropTypes.object.isRequired,
};

export default VariantSettingsPrompt;
